'use client';

import { c as _c } from "react/compiler-runtime";
import { useState, useRef, useCallback, forwardRef, type ReactNode, type RefObject, type RefCallback } from 'react';
import { usePopper } from 'react-popper';
import { motion } from 'motion/react';
import { cx } from 'class-variance-authority';
import useOnClickOutside from './hooks/useOnClickOutside';
import Portal from './Portal';
import { useEventListener } from './hooks/DOM';
export type PopoverProps = {
  /** @default 'span' */
  as?: 'span' | 'div' | 'button';
  /** @default 'bottom-start' */
  position?: NonNullable<Parameters<typeof usePopper>[2]>['placement'];
  /** @default 'click' */
  event?: 'click' | 'hover';
  content: ReactNode | ((props: {
    open: () => void;
    close: () => void;
  }) => ReactNode);
  hasArrow?: boolean;
  arrowClassName?: string;
  className?: string;
  /** @default '#popovers' */
  querySelector?: string;
  /** Delay in ms before showing the popover */
  delay?: number;
  children: ReactNode;
};
const PopoverContainerWithRef = forwardRef<HTMLDivElement, {
  onPressEscape?: () => void;
  onOutsideClick?: () => void;
  className?: string;
}>(
// eslint-disable-next-line prefer-arrow-callback
function PopoverContainer(t0, ref) {
  const $ = _c(13);
  let onOutsideClick;
  let onPressEscape;
  let props;
  if ($[0] !== t0) {
    ({
      onPressEscape,
      onOutsideClick,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = onOutsideClick;
    $[2] = onPressEscape;
    $[3] = props;
  } else {
    onOutsideClick = $[1];
    onPressEscape = $[2];
    props = $[3];
  }
  const containerRef = useRef(null);
  let t1;
  if ($[4] !== onPressEscape) {
    t1 = e => {
      if (e.key === "Escape") {
        onPressEscape?.();
      }
    };
    $[4] = onPressEscape;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  useEventListener("window", "keydown", t1);
  let t2;
  if ($[6] !== onOutsideClick) {
    t2 = () => onOutsideClick?.();
    $[6] = onOutsideClick;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  useOnClickOutside(containerRef as RefObject<HTMLElement>, t2);
  let t3;
  if ($[8] !== ref) {
    t3 = combineRefs(ref !== null ? [ref as RefObject<HTMLDivElement>, containerRef as RefObject<HTMLDivElement>] : [containerRef as RefObject<HTMLDivElement>]);
    $[8] = ref;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  let t4;
  if ($[10] !== props || $[11] !== t3) {
    t4 = <div ref={t3} {...props} />;
    $[10] = props;
    $[11] = t3;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  return t4;
});
const AnimatedPopoverContainer = motion.create(PopoverContainerWithRef, {
  forwardMotionProps: true
});
export default function Popover(t0) {
  const $ = _c(35);
  const {
    as: t1,
    position: t2,
    event: t3,
    content,
    hasArrow,
    arrowClassName,
    className,
    querySelector: t4,
    delay,
    children
  } = t0;
  const Component = t1 === undefined ? "span" : t1;
  const position = t2 === undefined ? "bottom-start" : t2;
  const event = t3 === undefined ? "click" : t3;
  const querySelector = t4 === undefined ? "#popovers" : t4;
  const [isOpen, setIsOpen] = useState(false);
  const delayTimerRef = useRef(null);
  const [popperElement, setPopperElement] = useState(null);
  const [referenceElement, setReferenceElement] = useState(null);
  const [arrowElement, setArrowElement] = useState(null);
  let t5;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = node => setReferenceElement(node);
    $[0] = t5;
  } else {
    t5 = $[0];
  }
  const referenceRef = t5;
  let t6;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = node_0 => setPopperElement(node_0);
    $[1] = t6;
  } else {
    t6 = $[1];
  }
  const popperRef = t6;
  let t7;
  if ($[2] !== arrowElement || $[3] !== hasArrow) {
    t7 = !hasArrow ? undefined : [{
      name: "arrow",
      options: {
        element: arrowElement
      }
    }, {
      name: "offset",
      enabled: true,
      options: {
        offset: [0, 8]
      }
    }];
    $[2] = arrowElement;
    $[3] = hasArrow;
    $[4] = t7;
  } else {
    t7 = $[4];
  }
  let t8;
  if ($[5] !== position || $[6] !== t7) {
    t8 = {
      strategy: "fixed",
      placement: position,
      modifiers: t7
    };
    $[5] = position;
    $[6] = t7;
    $[7] = t8;
  } else {
    t8 = $[7];
  }
  const popper = usePopper(referenceElement, popperElement, t8);
  let t9;
  if ($[8] !== className) {
    t9 = cx("focus:outline-hidden", className);
    $[8] = className;
    $[9] = t9;
  } else {
    t9 = $[9];
  }
  let t10;
  if ($[10] !== event) {
    t10 = e => {
      if (event !== "click") {
        return;
      }
      setIsOpen(true);
      e.stopPropagation();
    };
    $[10] = event;
    $[11] = t10;
  } else {
    t10 = $[11];
  }
  let t11;
  if ($[12] !== delay || $[13] !== event) {
    t11 = () => {
      if (event !== "hover") {
        return;
      }
      if (delay) {
        delayTimerRef.current = setTimeout(() => setIsOpen(true), delay);
      } else {
        setIsOpen(true);
      }
    };
    $[12] = delay;
    $[13] = event;
    $[14] = t11;
  } else {
    t11 = $[14];
  }
  let t12;
  if ($[15] !== event) {
    t12 = () => {
      if (event !== "hover") {
        return;
      }
      if (delayTimerRef.current) {
        clearTimeout(delayTimerRef.current);
        delayTimerRef.current = null;
      }
      setIsOpen(false);
    };
    $[15] = event;
    $[16] = t12;
  } else {
    t12 = $[16];
  }
  let t13;
  if ($[17] !== Component || $[18] !== children || $[19] !== t10 || $[20] !== t11 || $[21] !== t12 || $[22] !== t9) {
    t13 = <Component className={t9} ref={referenceRef} onClick={t10} onMouseEnter={t11} onMouseLeave={t12}>{children}</Component>;
    $[17] = Component;
    $[18] = children;
    $[19] = t10;
    $[20] = t11;
    $[21] = t12;
    $[22] = t9;
    $[23] = t13;
  } else {
    t13 = $[23];
  }
  let t14;
  if ($[24] !== arrowClassName || $[25] !== content || $[26] !== event || $[27] !== hasArrow || $[28] !== isOpen || $[29] !== popper || $[30] !== querySelector) {
    t14 = isOpen && <Portal querySelector={querySelector}><AnimatedPopoverContainer ref={popperRef} className={cx(event === "hover" && "pointer-events-none")} style={popper.styles.popper} initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} onOutsideClick={() => setIsOpen(false)} onPressEscape={() => setIsOpen(false)} {...popper.attributes.popper}>{hasArrow && <div ref={setArrowElement} style={popper.styles.arrow} className={arrowClassName} />}{typeof content === "function" ? content({
          open: () => setIsOpen(true),
          close: () => setIsOpen(false)
        }) : content}</AnimatedPopoverContainer></Portal>;
    $[24] = arrowClassName;
    $[25] = content;
    $[26] = event;
    $[27] = hasArrow;
    $[28] = isOpen;
    $[29] = popper;
    $[30] = querySelector;
    $[31] = t14;
  } else {
    t14 = $[31];
  }
  let t15;
  if ($[32] !== t13 || $[33] !== t14) {
    t15 = <>{t13}{t14}</>;
    $[32] = t13;
    $[33] = t14;
    $[34] = t15;
  } else {
    t15 = $[34];
  }
  return t15;
}
export const combineRefs = <T extends Element,>(refs: (RefObject<T> | RefCallback<T>)[] = []) => (el: T | null) => {
  refs.forEach(ref => {
    if (typeof ref === 'function') {
      ref(el);
    } else if (ref && ref.current !== undefined) {
      ref.current = el as T;
    }
  });
};